import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckIcon, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

interface PlanFeature {
  text: string;
}

interface Plan {
  key: string;
  badge: string;
  price: string;
  interval: string;
  description: string;
  ctaText: string;
  ctaAction: string;
  features: PlanFeature[];
}

const plans: Plan[] = [
  {
    key: "on-demand",
    badge: "On Demand",
    price: "$50",
    interval: "/ hour",
    description:
      "Pay only for the hours our agents work—10-hour minimum required",
    ctaText: "Hire now",
    ctaAction:
      "https://calendly.com/d/cntq-2xx-jzf/hire-a-human-consultation-50-deposit-required",
    features: [
      { text: "Our agents provide support whenever you need it" },
      { text: "Get help deploying and setting up your project" },
      { text: "Full stack support" },
    ],
  },
  {
    key: "agent-plus",
    badge: "Agent+",
    price: "$4000",
    interval: "/ month",
    description: "3-month minimum commitment",
    ctaText: "Learn more",
    ctaAction: "https://www.tempo.new/ai-agent-plus/book-call",
    features: [
      { text: "Our agents design and build 1-3 features per week" },
      { text: "Quality guaranteed by Human Engineers and Designers" },
      { text: "48-72hr turnaround time" },
      { text: "Unlimited design revisions and code review improvements" },
    ],
  },
];

// Trusted companies logos
const trustedLogos = [
  "/logos/prosperous.svg",
  "/logos/revel_ai.svg",
  "/logos/molg.svg",
  "/logos/chorus_ai.svg",
  "/logos/keye.svg",
];

interface Props {
  closeModal: () => void;
}

const HireHumanModal: React.FC<Props> = ({ closeModal }) => {
  // Add a ref to track the logo container
  const logoTrackRef = useRef<HTMLDivElement>(null);
  const [logoSetWidth, setLogoSetWidth] = useState(0);
  const [animationReady, setAnimationReady] = useState(false);

  // Calculate logo widths on mount and window resize
  useEffect(() => {
    // Calculate the total width of logos
    const calculateLogoSetWidth = () => {
      if (logoTrackRef.current) {
        // Get all logo items and calculate total width including gaps
        const logoItems = logoTrackRef.current.querySelectorAll(".logo-item");
        const gap = 32; // 2rem = 32px
        let totalWidth = 0;

        logoItems.forEach((item) => {
          totalWidth += (item as HTMLElement).offsetWidth + gap;
        });

        // Subtract the last gap
        if (logoItems.length > 0) totalWidth -= gap;

        if (totalWidth > 0) {
          setLogoSetWidth(totalWidth);
          setAnimationReady(true);
        }
      }
    };

    // Wait for images to load
    const imagePromises = Array.from(
      document.querySelectorAll(".logo-item")
    ).map((img) => {
      if ((img as HTMLImageElement).complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.addEventListener("load", resolve, { once: true });
        img.addEventListener("error", resolve, { once: true });
      });
    });

    // Calculate after all images have loaded
    Promise.all(imagePromises).then(() => {
      calculateLogoSetWidth();
    });

    // Recalculate on window resize
    const handleResize = () => {
      calculateLogoSetWidth();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleCtaClick = (ctaAction: string) => {
    // Navigate to the landing page
    window.open(ctaAction, "_blank");
  };
  return (
    <Dialog open={true} onOpenChange={closeModal}>
      <DialogContent
        className="bg-[#121214] text-white rounded-lg min-w-full h-full overflow-y-auto m-auto"
        hideClose={true}
      >
        <div className="absolute top-4 right-4">
          <Button
            onClick={closeModal}
            className="p-2 hover:bg-[#2F3035] rounded-md transition-colors"
          >
            <X className="w-5 h-5 text-[#F5F5F6]" />
          </Button>
        </div>

        <h1 className="text-[40px] text-center font-medium mt-8">
          Hire Expert Engineers & Designers
        </h1>

        <div className="space-y-6">
          {plans.map((plan) => (
            <div
              key={plan.key}
              className="rounded-xl bg-gradient-to-b from-[#1F1F22] to-[#131519] p-6 my-7 max-w-[700px] mx-auto"
            >
              <Badge
                variant="outline"
                className="w-fit px-3 py-1 text-xs mb-4 text-text-primary border-[#2F3035] rounded-[4px] bg-[#2F3035] font-normal"
              >
                {plan.badge}
              </Badge>

              <div className="grid grid-cols-12 gap-12">
                {/* Left side: Price and info */}
                <div className="col-span-5">
                  <div className="flex gap-2 mb-2 items-center">
                    <span className="text-5xl font-medium text-white">
                      {plan.price}
                    </span>
                    <span className="text-lg text-[#9F9FA4]">
                      {plan.interval}
                    </span>
                  </div>

                  <p className="text-sm text-[#9F9FA4] mb-4 whitespace-pre-line text-center">
                    {plan.description}
                  </p>

                  <Button
                    className={`w-full ${
                      plan.key === "on-demand"
                        ? "bg-[#6E54F4] hover:bg-[#5E44E4]"
                        : "bg-[#4CAF50] hover:bg-[#3C9F40]"
                    } text-white`}
                    onClick={() => handleCtaClick(plan.ctaAction)}
                  >
                    {plan.ctaText}
                  </Button>
                </div>

                {/* Right side: Feature list */}
                <div className="col-span-7 space-y-3 mt-0">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <div
                        className={`flex-shrink-0 rounded-sm w-5 h-5 flex items-center justify-center ${
                          plan.key === "on-demand"
                            ? "bg-[#6E54F4]"
                            : "bg-[#4CAF50]"
                        }`}
                      >
                        <CheckIcon className="h-3.5 w-3.5 text-white" />
                      </div>
                      <span className="text-sm text-white">{feature.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="py-4">
          <p className="text-2xl text-center my-8">
            <span className="text-[#9F9FA4]">Trusted by the </span>
            <span className="font-normal">fastest growing teams</span>
          </p>

          <div
            className="logo-carousel overflow-hidden mx-auto relative"
            style={{
              width: `${logoSetWidth / 2}px`,
            }}
          >
            <div
              className={`logo-track ${animationReady ? "animate-scroll" : ""}`}
              ref={logoTrackRef}
            >
              <div className="logo-set flex gap-8">
                {[...trustedLogos, ...trustedLogos].map((logo, idx) => (
                  <img
                    key={`logo-set1-${idx}`}
                    src={logo}
                    alt="Trusted company logo"
                    className="logo-item"
                  />
                ))}
              </div>
            </div>
            <div className="absolute top-0 left-0 h-full w-16 bg-gradient-to-r from-background-primary to-transparent"></div>
            <div className="absolute top-0 right-0 h-full w-16 bg-gradient-to-l from-background-primary to-transparent"></div>
          </div>

          <style>{`
            .logo-carousel {
              width: 100%;
              overflow: hidden;
              position: relative;
              padding: 10px 0;
            }
            
            .logo-track {
              display: flex;
              gap: 2rem;
              width: fit-content;
            }
            
            .logo-track.animate-scroll {
              animation: scroll 30s linear infinite;
            }
            
            .logo-item {
              height: 32px;
              flex-shrink: 0;
              object-fit: contain;
            }

            @keyframes scroll {
              0% {
                transform: translateX(0);
              }
              100% {
                transform: translateX(-${logoSetWidth / 2}px);
              }
            }
          `}</style>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HireHumanModal;
